/**
 * Platform-admin client for launcher curation (plan §3 / Phase 5).
 *
 * Every call here goes to a `requirePlatformAdmin` route, which needs the caller
 * to hold `platformRole === 'admin'` AND be ACTING IN the platform org. The
 * screen gates itself on `useBudgetBillingAccess`, which mirrors that predicate
 * exactly — but this module assumes nothing: a 403 surfaces as a thrown error
 * with the server's message, because the client gate only decides whether to
 * offer a door, never whether it opens.
 */
import { request } from '@papan/api-client'

/** One app the admin can pin, as `GET …/apps` and `GET …/landing-shortcuts` shape it. */
export interface AdminLauncherApp {
  fileId: string
  /** Display name — pointer suffix and `.miniapp` stripped. */
  name: string
  /** The raw row name, so two apps that display identically stay tellable apart
   *  (`Sales.miniapp` vs a mounted `Sales.miniapp.shared`). */
  rawName: string
  /** `.shared` pointer — an app mounted from another tenant. Its liveness and
   *  its icon assets both depend on a grant this deployment does not control. */
  mounted: boolean
  /** `{ miniappIcon, miniappIconRev }` JSON, or null for the default squircle. */
  icon: string | null
}

/** A currently-pinned row. Carries the app fields too, EXCEPT when `missing`. */
export interface AdminLauncherShortcut extends Partial<AdminLauncherApp> {
  fileId: string
  label: string | null
  position: number
  createdBy: string | null
  /**
   * The FK kept this row but the target stopped being an app — renamed out of
   * `.miniapp`, or a pointer demoted to a plain file. The user-facing read hides
   * these; this screen is the only place they can be found and cleaned up, so
   * the editor must render them rather than filtering them out.
   */
  missing: boolean
}

/**
 * Just enough of a tenant to pick one.
 *
 * Deliberately structural and narrow rather than importing `AdminTenantRow`
 * from `@papan/orgs/client`: that would make @papan/launcher depend on
 * @papan/orgs for three fields, and the row it describes carries per-tenant
 * spend aggregates this screen has no use for.
 */
export interface AdminTenantOption {
  id: string
  name: string
  type: string
  /** Personal tenant: the account's main email (absent on older responses). */
  primaryEmail?: string | null
  /** `false` = the account never proved its main email. */
  primaryEmailVerified?: boolean | null
  /** Organization: owner emails / still-invited owner emails. */
  ownerEmails?: string[]
  invitedOwnerEmails?: string[]
}

export const launcherAdminApi = {
  /**
   * Tenant picker source — the SAME `GET /api/admin/tenants` the Tenant Budgets
   * page paginates, so "which tenants exist" has one answer across the shelf.
   * It computes a period spend aggregate we ignore; at picker page sizes that is
   * cheaper than a second tenant-enumeration endpoint that could disagree with
   * the first about what a tenant is.
   */
  async listTenants(opts: { limit?: number; offset?: number; search?: string; type?: 'organization' | 'personal' } = {}) {
    const q = new URLSearchParams()
    if (opts.limit != null) q.set('limit', String(opts.limit))
    if (opts.offset != null) q.set('offset', String(opts.offset))
    if (opts.type) q.set('type', opts.type)
    if (opts.search?.trim()) q.set('q', opts.search.trim())
    const qs = q.toString()
    return request<{ tenants: AdminTenantOption[]; total: number; limit: number; offset: number }>(
      `/api/admin/tenants${qs ? `?${qs}` : ''}`,
    )
  },

  /** Every app in the tenant that could be pinned. */
  async listApps(tenantId: string) {
    return request<{ tenantId: string; apps: AdminLauncherApp[] }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/apps`,
    )
  },

  /** The tenant's current launcher — the CONFIG, unfiltered by anyone's permission. */
  async getShortcuts(tenantId: string) {
    return request<{ tenantId: string; shortcuts: AdminLauncherShortcut[] }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/landing-shortcuts`,
    )
  },

  /**
   * Replace the whole ordered set. `position` is the array index — the server
   * derives it, so the client must never send one.
   */
  async putShortcuts(tenantId: string, shortcuts: Array<{ fileId: string; label: string | null }>) {
    return request<{ success: true; tenantId: string; shortcuts: Array<{ fileId: string; label: string | null; position: number }> }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/landing-shortcuts`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shortcuts }),
      },
    )
  },
}
